import { useState, useEffect, useRef } from "react";
import { MOOD_LABELS } from "../utils/mood";
import { readSingleDelta } from "../utils/activation";
import { isAcuteReading } from "../lib/crisis";
import CrisisSupport from "./CrisisSupport";
import { hapticLight, hapticSuccess } from "../utils/haptics";
import "../styles/ritual.css";

/**
 * ONE READING, FULL SCREEN
 * ===========================================================================
 * Used twice, at the two ends of an activation:
 *
 *   mode="before"  — tapped as they start. Writes a true pre_session reading.
 *   mode="after"   — tapped as they finish. Writes post_session, then shows
 *                    the patient what the two readings did.
 *
 * WHY "BEFORE" EXISTS AT ALL
 * The evidence engine can fall back to the morning check-in as the baseline,
 * and for a while that was the only baseline this product ever had. It is
 * confounded and the confound is large: an evening walk credited against a
 * reading taken at 8am is credited with eleven hours of ordinary life —
 * lunch, weather, other people, the passage of the day itself. Mood rises
 * through the morning in most people whatever they do.
 *
 * The strict pair — a reading taken minutes before the activation and minutes
 * after it — is the only comparison this product can actually defend, and it
 * is the one the clinician is going to repeat aloud in a session. So it gets
 * its own screen and its own tap, and the fallback stays as a fallback:
 * pairSessions() prefers a real pre_session over the daily whenever one
 * exists (already true, already tested), so this simply gives it something
 * better to find.
 *
 * It is one tap and it is skippable. A patient who does not take it still
 * generates a usable pair via the fallback; they just generate a weaker one,
 * and the app knows which is which.
 */

const MOOD_ORDER = [5, 4, 3, 2, 1];

function prefersReducedMotion() {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

/** When the baseline was taken, in words, so no screen has to guess. */
export function whenTaken(at) {
  if (!at) return "earlier";
  const d = new Date(at);
  if (Number.isNaN(d.getTime())) return "earlier";
  const h = d.getHours();
  if (h < 5) return "overnight";
  if (h < 12) return "this morning";
  if (h < 17) return "this afternoon";
  return "this evening";
}

/** Whether the baseline is close enough in time to be worth comparing hard. */
const IMMEDIATE_WINDOW_MS = 4 * 3600 * 1000;

export function isImmediate(beforeAt, now = Date.now()) {
  if (!beforeAt) return false;
  const t = new Date(beforeAt).getTime();
  return !Number.isNaN(t) && now - t <= IMMEDIATE_WINDOW_MS;
}

/**
 * Before and after on the scale the patient already tapped. Both dots the same
 * size: drawing the "after" larger made every reading look like an improvement,
 * including the ones that were not.
 */
function DeltaArc({ before, after }) {
  const pct = (v) => ((v - 1) / 4) * 100;
  return (
    <div className="rit-arc" aria-hidden="true">
      <div className="rit-arc-track" />
      <div
        className="rit-arc-span"
        style={{
          left: `${Math.min(pct(before), pct(after))}%`,
          width: `${Math.abs(pct(after) - pct(before))}%`,
        }}
      />
      <span className="rit-arc-dot rit-arc-dot--before" data-mood={before} style={{ left: `${pct(before)}%` }} />
      <span className="rit-arc-dot rit-arc-dot--after" data-mood={after} style={{ left: `${pct(after)}%` }} />
    </div>
  );
}

export default function MoodMoment({
  mode = "after",
  what,
  beforeMood,
  beforeAt,
  pairedSoFar = 0,
  pairsNeeded = 3,
  onSave,
  onDone,
}) {
  const asking = mode === "before";
  const [step, setStep] = useState("ask");
  const [value, setValue] = useState(null);
  const savedRef = useRef(false);
  const timer = useRef(null);
  const headingRef = useRef(null);
  const reduced = useRef(prefersReducedMotion());

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = prev;
    };
  }, []);

  useEffect(() => () => clearTimeout(timer.current), []);

  // The stage remounts per step, dropping focus to <body>. Moving focus to the
  // new heading restores the tab position and announces the step — which is
  // why there is no aria-live here.
  useEffect(() => {
    headingRef.current?.focus();
  }, [step]);

  const choose = (v) => {
    if (savedRef.current) return;
    savedRef.current = true;
    hapticLight();
    setValue(v);
    // Written immediately: if they close the phone on the next screen, the
    // reading is already recorded. The data is the point, the animation is not.
    Promise.resolve(onSave?.(v)).catch(() => {});

    // The "before" reading has nothing to reveal — it goes straight out of the
    // way so the patient can get on with the thing they said they'd do.
    if (asking) {
      onDone?.();
      return;
    }

    clearTimeout(timer.current);
    if (reduced.current) {
      setStep("reveal");
    } else {
      timer.current = setTimeout(() => {
        hapticSuccess();
        setStep("reveal");
      }, 640);
    }
  };

  const reading = step === "reveal" ? readSingleDelta(beforeMood, value) : null;
  const canCompare = beforeMood != null && value != null;
  const immediate = isImmediate(beforeAt);
  const acute = isAcuteReading(value);
  const remaining = Math.max(0, pairsNeeded - (pairedSoFar + 1));

  return (
    <main
      className="rit rit--close"
      id="main-content"
      data-mood={value ?? 0}
      data-step={step}
      aria-label={asking ? "Before you start" : "How the activation went"}
    >
      <div className="rit-sky" aria-hidden="true" />
      <div className="rit-bloom" aria-hidden="true" />

      <div className="rit-chrome">
        <span />
        {step === "ask" && (
          <button type="button" className="rit-skip" onClick={onDone}>
            {asking ? "Skip" : "Later"}
          </button>
        )}
      </div>

      <div className="rit-stage" key={step}>
        {step === "ask" && (
          <>
            <header className="rit-head">
              <p className="rit-eyebrow">{asking ? "Before you go" : `Done — ${what}`}</p>
              <h1 className="rit-q" ref={headingRef} tabIndex={-1}>
                {asking ? "Where are you right now?" : "How are you now?"}
              </h1>
              <p className="rit-sub">
                {asking
                  ? "One tap, so there's something honest to compare it to afterwards."
                  : "One tap. It goes in your record either way."}
              </p>
            </header>

            <div className="rit-moods" role="group" aria-label={asking ? "Mood before" : "Mood now"}>
              {MOOD_ORDER.map((v) => (
                <button
                  key={v}
                  type="button"
                  className="rit-mood"
                  data-mood={v}
                  aria-pressed={value === v}
                  onClick={() => choose(v)}
                >
                  <span className="rit-orb" data-mood={v} aria-hidden="true" />
                  <span className="rit-mood-label">{MOOD_LABELS[v - 1]}</span>
                </button>
              ))}
            </div>
          </>
        )}

        {step === "reveal" && (
          <>
            <header className="rit-head">
              <p className="rit-eyebrow">{what}</p>
              {/* The behaviour is the target. The delta is information about
                  it, never the verdict on it. */}
              <h1 className="rit-q" ref={headingRef} tabIndex={-1}>
                That&apos;s done.
              </h1>
            </header>

            {/* The bottom of the scale gets support on this single reading —
                no history required, no model in the path. */}
            {acute && <CrisisSupport variant="crisis" />}

            {canCompare ? (
              <div className="rit-reading">
                <DeltaArc before={beforeMood} after={value} />
                <p className="rit-reading-line">{reading.line}</p>
                <p className="rit-reading-detail">
                  <span>
                    {MOOD_LABELS[beforeMood - 1]} {immediate ? "before you started" : whenTaken(beforeAt)}
                  </span>
                  <span className="rit-reading-sep" aria-hidden="true">→</span>
                  <span>{MOOD_LABELS[value - 1]} now</span>
                </p>

                {/* A comparison against a reading taken hours earlier is
                    credited with the whole day in between, and the patient is
                    entitled to know which kind they are looking at. */}
                {!immediate && (
                  <p className="rit-caveat">
                    That earlier reading was a while ago, so a lot of the day
                    sits between the two. Tapping &ldquo;Starting now&rdquo;
                    next time gives you a cleaner comparison.
                  </p>
                )}
              </div>
            ) : (
              <p className="rit-sub">
                Recorded. Once there&apos;s an earlier reading to compare it
                with, this starts building into something.
              </p>
            )}

            {/* The honest thing for this space: where the record actually
                stands. A count, not a promise, and never a progress bar —
                this is not a target the patient is failing to hit. */}
            {canCompare && (
              <p className="rit-progress">
                {remaining > 0
                  ? `That's ${pairedSoFar + 1} reading${pairedSoFar + 1 === 1 ? "" : "s"} with a before and an after. At ${pairsNeeded}, the app can start saying something about you rather than about today.`
                  : "You've got enough readings now for the pattern to mean something. It's on your home screen."}
              </p>
            )}

            <div className="rit-actions">
              <button className="btn btn-primary btn-full" onClick={onDone}>
                Close
              </button>
            </div>
          </>
        )}
      </div>
    </main>
  );
}
